import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, UploadCloud, AlertTriangle, CheckCircle } from 'lucide-react';
import { apiClient, type AdvboxImportResult, type DocumentTemplateCategory } from '@/lib/api-client';
import { Button } from '@/components/ui/button';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function FileDropzone({
  file,
  onChange,
}: {
  file: File | null;
  onChange: (f: File | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function handleFiles(files: FileList | null) {
    const f = files?.[0] ?? null;
    if (f && f.type !== 'application/pdf' && !f.name.endsWith('.pdf')) return;
    onChange(f);
  }

  return (
    <div
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={(e) => { e.preventDefault(); setDragging(false); handleFiles(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      className={[
        'flex cursor-pointer flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-10 transition',
        dragging
          ? 'border-[var(--accent)] bg-[color-mix(in_oklch,var(--accent)_8%,transparent)]'
          : 'border-[var(--border)] hover:border-[var(--accent)]',
      ].join(' ')}
    >
      <UploadCloud className="h-8 w-8 text-[var(--fg-muted)]" />
      {file ? (
        <p className="text-sm font-medium text-[var(--fg)]">{file.name}</p>
      ) : (
        <>
          <p className="text-sm font-medium text-[var(--fg)]">
            Arraste o PDF aqui ou clique para selecionar
          </p>
          <p className="text-xs text-[var(--fg-muted)]">Apenas arquivos .pdf</p>
        </>
      )}
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function ImportacaoTemplatesPage() {
  const navigate = useNavigate();

  const [file, setFile] = useState<File | null>(null);
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [categories, setCategories] = useState<DocumentTemplateCategory[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<AdvboxImportResult | null>(null);

  // Carrega categorias para o select
  useEffect(() => {
    apiClient
      .listDocumentTemplateCategories()
      .then((cats) => setCategories(cats))
      .catch(() => { /* silencioso */ });
  }, []);

  // Atualiza name ao selecionar arquivo (se name ainda vazio)
  useEffect(() => {
    if (file && !name.trim()) {
      setName(file.name.replace(/\.pdf$/i, '').trim());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!file) { setError('Selecione um arquivo PDF.'); return; }
    if (!name.trim()) { setError('Informe o nome do modelo.'); return; }

    setError(null);
    setLoading(true);
    try {
      const res = await apiClient.documentTemplatesImportAdvboxPdf(
        file,
        name.trim(),
        categoryId || null,
      );
      setResult(res);
    } catch (err: unknown) {
      const msg =
        err instanceof Error ? err.message : 'Erro ao importar o PDF.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  // ---------------------------------------------------------------------------
  // Tela de resultado
  // ---------------------------------------------------------------------------

  if (result) {
    const { template, stats } = result;
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={() => setResult(null)}
          className="flex items-center gap-1 text-sm text-[var(--fg-muted)] hover:text-[var(--fg)]"
        >
          <ChevronLeft className="h-4 w-4" />
          Importar outro modelo
        </button>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-4">
          <div className="flex items-center gap-3">
            <CheckCircle className="h-6 w-6 text-green-500" />
            <h2 className="text-lg font-semibold text-[var(--fg)]">
              Modelo importado com sucesso
            </h2>
          </div>

          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-[var(--fg-muted)]">Nome</dt>
            <dd className="text-[var(--fg)] font-medium">{template.name}</dd>
            <dt className="text-[var(--fg-muted)]">Variáveis detectadas</dt>
            <dd className="text-[var(--fg)] font-medium">{stats.variablesDetected}</dd>
          </dl>

          {stats.unmappedTokens.length > 0 && (
            <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/10 p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-yellow-700 dark:text-yellow-300">
                <AlertTriangle className="h-4 w-4" />
                {stats.unmappedTokens.length} token{stats.unmappedTokens.length !== 1 ? 's' : ''} não mapeado{stats.unmappedTokens.length !== 1 ? 's' : ''} — mantidos como texto
              </div>
              <ul className="list-disc list-inside space-y-0.5 text-xs text-[var(--fg-muted)]">
                {stats.unmappedTokens.map((t) => (
                  <li key={t}>{t}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() =>
                navigate(`/escritorio/documentos/${template.id}/editor`)
              }
            >
              Abrir no editor
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/escritorio/documentos')}
            >
              Ver modelos
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Formulario
  // ---------------------------------------------------------------------------

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => navigate('/escritorio/configuracoes/importacao')}
          className="flex items-center gap-1 text-sm text-[var(--fg-muted)] hover:text-[var(--fg)]"
        >
          <ChevronLeft className="h-4 w-4" />
          Importação
        </button>
      </div>

      <div>
        <h1 className="text-xl font-semibold text-[var(--fg)]">
          Importar modelos de documento
        </h1>
        <p className="mt-1 text-sm text-[var(--fg-muted)]">
          Importe um modelo de documento PDF exportado do ADVBOX. As variáveis
          [[...]] serão convertidas automaticamente para o formato VoxisAdv.
        </p>
      </div>

      <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
        <div className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-6 space-y-5">
          {/* Upload */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-[var(--fg)]">
              Arquivo PDF
            </label>
            <FileDropzone file={file} onChange={setFile} />
          </div>

          {/* Nome */}
          <div className="space-y-1.5">
            <label
              htmlFor="template-name"
              className="text-sm font-medium text-[var(--fg)]"
            >
              Nome do modelo <span className="text-red-500">*</span>
            </label>
            <input
              id="template-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ex: Contrato de honorários"
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] placeholder:text-[var(--fg-muted)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            />
          </div>

          {/* Categoria */}
          <div className="space-y-1.5">
            <label
              htmlFor="template-category"
              className="text-sm font-medium text-[var(--fg)]"
            >
              Categoria <span className="text-[var(--fg-muted)] font-normal">(opcional)</span>
            </label>
            <select
              id="template-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              className="w-full rounded-lg border border-[var(--border)] bg-[var(--bg)] px-3 py-2 text-sm text-[var(--fg)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
            >
              <option value="">Sem categoria</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Erro */}
          {error && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-700 dark:text-red-300">
              {error}
            </div>
          )}

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/escritorio/configuracoes/importacao')}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={loading || !file}>
              {loading ? 'Importando...' : 'Importar modelo'}
            </Button>
          </div>
        </div>
      </form>

      <div className="rounded-lg border border-[var(--border)] bg-[var(--surface)] p-4 text-xs text-[var(--fg-muted)] space-y-1">
        <p className="font-medium text-[var(--fg)]">Limitações conhecidas</p>
        <ul className="list-disc list-inside space-y-0.5">
          <li>Imagens no PDF são ignoradas — adicione-as manualmente no editor</li>
          <li>Layout em 2 colunas vira texto sequencial (1 coluna)</li>
          <li>Alinhamento de tabelas pode perder formatação — revise no TipTap</li>
          <li>Fontes e tamanhos não são preservados</li>
        </ul>
      </div>
    </div>
  );
}
